"use server"

import { sdk } from "@lib/config"
import { sortProducts } from "@lib/util/sort-products"
import { HttpTypes } from "@medusajs/types"
import { SortOptions } from "@modules/store/components/refinement-list/sort-products"
import { getAuthHeaders, getCacheOptions } from "./cookies"
import { getRegion, retrieveRegion } from "./regions"
import { getAvailabilityStatus } from "@lib/util/product-availability"
import { getLocale } from "next-intl/server"

export const listProducts = async ({
  pageParam = 1,
  queryParams,
  countryCode,
  regionId,
}: {
  pageParam?: number
  queryParams?: HttpTypes.FindParams & HttpTypes.StoreProductListParams
  countryCode?: string
  regionId?: string
}): Promise<{
  response: { products: HttpTypes.StoreProduct[]; count: number }
  nextPage: number | null
  queryParams?: HttpTypes.FindParams & HttpTypes.StoreProductListParams
}> => {
  if (!countryCode && !regionId) {
    throw new Error("Country code or region ID is required")
  }

  const limit = queryParams?.limit || 12
  const _pageParam = Math.max(pageParam, 1)
  const offset = _pageParam === 1 ? 0 : (_pageParam - 1) * limit

  let region: HttpTypes.StoreRegion | undefined | null

  if (countryCode) {
    region = await getRegion(countryCode)
  } else {
    region = await retrieveRegion(regionId!)
  }

  if (!region) {
    return {
      response: { products: [], count: 0 },
      nextPage: null,
    }
  }

  const headers = {
    ...(await getAuthHeaders()),
  }

  const next = {
    ...(await getCacheOptions("products")),
  }

  const locale = await getLocale()

  return sdk.client
    .fetch<{ products: HttpTypes.StoreProduct[]; count: number }>(
      `/store/products`,
      {
        method: "GET",
        query: {
          limit,
          offset,
          region_id: region?.id,
          locale,
          fields:
            "*variants.calculated_price,+variants.inventory_quantity,*variants.images,*images,+thumbnail,+metadata,+tags,",
          ...queryParams,
        },
        headers,
        next: { ...next, revalidate: 300 },
      }
    )
    .then(({ products, count }) => {
      const nextPage = count > offset + limit ? pageParam + 1 : null

      return {
        response: {
          products,
          count,
        },
        nextPage: nextPage,
        queryParams,
      }
    })
}

/**
 * This will fetch 100 products to the Next.js cache and sort them based on the sortBy parameter.
 * It will then return the paginated products based on the page and limit parameters.
 */
export const listProductsWithSort = async ({
  page = 0,
  queryParams,
  sortBy = "created_at",
  countryCode,
}: {
  page?: number
  queryParams?: HttpTypes.FindParams & HttpTypes.StoreProductParams
  sortBy?: SortOptions
  countryCode: string
}): Promise<{
  response: { products: HttpTypes.StoreProduct[]; count: number }
  nextPage: number | null
  queryParams?: HttpTypes.FindParams & HttpTypes.StoreProductParams
}> => {
  const limit = queryParams?.limit || 12

  const fetchLimit = Math.max(limit, 100)

  const {
    response: { products, count },
  } = await listProducts({
    pageParam: 0,
    queryParams: {
      ...queryParams,
      limit: fetchLimit,
    },
    countryCode,
  })

  const sortedProducts = sortProducts(products, sortBy)

  const pageParam = (page - 1) * limit

  const nextPage = count > pageParam + limit ? pageParam + limit : null

  const paginatedProducts = sortedProducts.slice(pageParam, pageParam + limit)

  return {
    response: {
      products: paginatedProducts,
      count,
    },
    nextPage,
    queryParams,
  }
}

/**
 * Get popular/featured products for homepage
 * Returns products sorted by featured flag or sales count
 * @param countryCode - Country code for region
 * @param limit - Number of products to return (default: 12)
 */
export const getPopularProducts = async ({
  countryCode,
  limit = 12,
}: {
  countryCode: string
  limit?: number
}): Promise<HttpTypes.StoreProduct[]> => {
  // Fetch a reasonable amount — enough to find 12 after filtering
  // Previously fetched 100 products, each going through rental pricing + translation enrichment
  const fields = "*variants.calculated_price,+variants.inventory_quantity,*variants.images,*images,+thumbnail,+metadata,+tags,"

  const {
    response: { products },
  } = await listProducts({
    countryCode,
    queryParams: {
      limit: limit * 3, // 36 instead of 100 — enough buffer for filtering
      fields,
      order: "-created_at",
    },
  })

  // Filter out unavailable products
  const availableProducts = products.filter(
    (p) => getAvailabilityStatus(p) !== "out-of-stock"
  )

  // Filter and sort by featured flag in metadata
  const featuredProducts = availableProducts
    .filter((p) => p.metadata?.featured === true)
    .slice(0, limit)

  // If not enough featured products, add recent products
  if (featuredProducts.length < limit) {
    const recentProducts = availableProducts
      .filter((p) => !p.metadata?.featured)
      .slice(0, limit - featuredProducts.length)

    return [...featuredProducts, ...recentProducts]
  }

  return featuredProducts
}

/**
 * Get new arrival products for homepage
 * Returns most recently created products
 * @param countryCode - Country code for region
 * @param limit - Number of products to return (default: 12)
 */
export const getNewArrivals = async ({
  countryCode,
  limit = 12,
}: {
  countryCode: string
  limit?: number
}): Promise<HttpTypes.StoreProduct[]> => {
  const {
    response: { products },
  } = await listProducts({
    countryCode,
    queryParams: {
      limit,
      fields:
        "*variants.calculated_price,+variants.inventory_quantity,*variants.images,*images,+thumbnail,+metadata,+tags,",
    },
  })

  // Sort by created_at (most recent first)
  return products.sort(
    (a, b) =>
      new Date(b.created_at!).getTime() - new Date(a.created_at!).getTime()
  )
}

/**
 * Server action: fetch a product by handle. Used by client components
 * that can't call Medusa SDK directly (CORS on dev, leaking
 * MEDUSA_BACKEND_URL on client). Example: QuickRentDialog needs the
 * full product (with variants/prices) when opened from a card.
 */
export const getProductByHandle = async ({
  handle,
  regionId,
  countryCode,
}: {
  handle: string
  regionId?: string
  countryCode?: string
}): Promise<HttpTypes.StoreProduct | null> => {
  let region: HttpTypes.StoreRegion | null | undefined
  if (regionId) {
    region = await retrieveRegion(regionId)
  } else if (countryCode) {
    region = await getRegion(countryCode)
  }

  const headers = {
    ...(await getAuthHeaders()),
  }
  const next = {
    ...(await getCacheOptions("products")),
  }
  const locale = await getLocale()

  const result = await sdk.client
    .fetch<{ products: HttpTypes.StoreProduct[] }>(`/store/products`, {
      method: "GET",
      query: {
        handle,
        limit: 1,
        ...(region?.id ? { region_id: region.id } : {}),
        locale,
        fields:
          "*variants.calculated_price,*variants.images,+variants.inventory_quantity,*images,+thumbnail,+metadata,+tags",
      },
      headers,
      next,
    })
    .catch(() => null)

  return result?.products?.[0] || null
}
